import { type Column, type ColumnDef } from "@tanstack/react-table";
import { type TFunction } from "i18next";

import { LAST_ACTIVE_SOURCE_TRANSLATION_KEYS, SEARCHABLE_AGGREGATOR_FIELDS } from "./constants";
import { type AggregatorColumnMeta, type AggregatorListItem, type MetricTone } from "./types";

export const getColumnMeta = (columnDef: ColumnDef<AggregatorListItem, unknown>) =>
  columnDef.meta as AggregatorColumnMeta | undefined;

export const getColumnSortAria = (column: Column<AggregatorListItem, unknown>) => {
  const sortDirection = column.getIsSorted();

  if (sortDirection === "asc") {
    return "ascending";
  }

  if (sortDirection === "desc") {
    return "descending";
  }

  return undefined;
};

const normalizeSearchValue = (value: string) => value.trim().toLocaleLowerCase();

export const getAggregatorName = (aggregator: AggregatorListItem) => aggregator.name?.trim() || "";

export const getAggregatorArea = (aggregator: AggregatorListItem) => aggregator.area?.trim() || "";

export const getFiniteNumber = (value: number | null | undefined) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  return undefined;
};

export const getMetricTone = (value: number): MetricTone => {
  if (value > 0 && value < 10) return "warning";
  return "default";
};

export const getLastActiveTimestamp = (aggregator: AggregatorListItem) => {
  if (!aggregator.last_active) {
    return undefined;
  }

  const timestamp = new Date(aggregator.last_active).getTime();
  return Number.isNaN(timestamp) ? undefined : timestamp;
};

export const getLastActiveSourceLabel = (
  source: string | null | undefined,
  t: TFunction
): string => {
  const code = source?.trim();

  if (!code) {
    return "";
  }

  const translationKey = LAST_ACTIVE_SOURCE_TRANSLATION_KEYS[code];

  return translationKey ? t(translationKey) : "";
};

export const filterAggregators = (aggregators: AggregatorListItem[], searchTerm?: string) => {
  const query = normalizeSearchValue(searchTerm ?? "");

  if (!query) {
    return aggregators;
  }

  return aggregators.filter(aggregator =>
    SEARCHABLE_AGGREGATOR_FIELDS.some(field =>
      normalizeSearchValue(aggregator[field] ?? "").includes(query)
    )
  );
};

export const getAggregatorRowId = (row: AggregatorListItem, index: number) =>
  row.id === undefined || row.id === null
    ? `${index}-${row.name ?? ""}-${row.area ?? ""}`
    : String(row.id);
